import { PhoneForwarded, PhoneIncoming, FileText, Play, Filter } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Button } from "@/components/ui/button";
import { Analytics } from "@/lib/analytics";
import { signupUrl } from "@/lib/productApp";
import type { AdsMode } from "./AdsModeSelector";

const venteSteps = [
  {
    icon: PhoneForwarded,
    title: "Activez le renvoi d'appel",
    description:
      "En débordement ou en renvoi total, vous choisissez. Le renvoi est actif en 30 secondes.",
  },
  {
    icon: PhoneIncoming,
    title: "L'appel est pris en charge",
    description:
      "Immovox se présente au nom de votre agence. Le besoin du prospect est qualifié : projet, budget, urgence.",
  },
  {
    icon: FileText,
    title: "Vous rappelez avec toutes les cartes en main",
    description:
      "E-mail avec le résumé de l'appel, l'intention détectée, le budget et les coordonnées. Prêt à être rappelé.",
  },
];

const locationSteps = [
  {
    icon: PhoneForwarded,
    title: "Choisissez votre mode",
    description:
      "Renvoi total pour déléguer 100 % des appels, ou débordement pour ne transférer que ceux que vous ne décrochez pas. C'est vous qui décidez.",
  },
  {
    icon: Filter,
    title: "Immovox questionne et qualifie",
    description:
      "Chaque appelant est accueilli au nom de votre agence. Solvabilité, garant, disponibilité : toutes les informations sont collectées.",
  },
  {
    icon: FileText,
    title: "Vous ne gérez que les bons dossiers",
    description:
      "Vous recevez une fiche structurée par e-mail. Plus besoin de trier 35 appels : vous rappelez uniquement les profils qualifiés.",
  },
];

const venteResult = {
  text: "Résultat : votre prospect a eu une réponse. Vous avez son projet, son budget et ses coordonnées.",
  cta: "Tester gratuitement",
  ctaKey: "activer_filet",
};

const locationResult = {
  text: "Résultat : vous passez de 35 appels à traiter à 5 dossiers qualifiés. Le reste, Immovox s'en occupe.",
  cta: "Tester gratuitement",
  ctaKey: "activer_bouclier",
};

const HowItWorksContent = ({ mode }: { mode: AdsMode }) => {
  const containerRef = useScrollReveal({ staggerDelay: 150 });
  const isVente = mode === "vente";
  const steps = isVente ? venteSteps : locationSteps;
  const result = isVente ? venteResult : locationResult;

  return (
    <div ref={containerRef} className="max-w-2xl mx-auto relative">
      <div className="absolute left-5 md:left-6 top-8 bottom-8 w-px bg-border animate-line-fill" />

      <div className="space-y-8">
        {steps.map((step, index) => (
          <div
            key={step.title}
            data-scroll-item
            className="scroll-reveal-item relative flex items-start gap-5 md:gap-6"
          >
            <div className="relative z-10 shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-lg shadow-warm ring-4 ring-background">
              {index + 1}
            </div>

            <div className="card-stripe p-5 flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="icon-badge shrink-0">
                  <step.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-foreground text-lg">
                  {step.title}
                </h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                {step.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-16 p-8 rounded-2xl bg-primary/5 border border-primary/10 text-center animate-fade-up">
        <h4 className="text-xl font-semibold text-foreground mb-4">
          {result.text}
        </h4>
        <Button
          size="lg"
          className="h-14 px-8 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-warm"
          asChild
        >
          <a
            href={signupUrl()}
            onClick={() => Analytics.ctaClicked(result.ctaKey, "ads_how_it_works")}
          >
            <Play className="w-5 h-5 mr-2" />
            {result.cta}
          </a>
        </Button>
      </div>
    </div>
  );
};

const AdsHowItWorks = ({ mode }: { mode: AdsMode }) => {
  const isVente = mode === "vente";

  return (
    <section className="section-padding bg-secondary">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-medium text-sm mb-6">
            Comment ça marche
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Simplicité radicale
          </h2>
          <p className="text-muted-foreground leading-relaxed text-[15px] md:text-base">
            {isVente
              ? "Rien à installer. Rien à changer. Vous activez, on décroche."
              : "Rien à installer. Rien à changer. Vous activez, on s'occupe du reste."}
          </p>
        </div>

        {/* key forces full remount so useScrollReveal re-observes */}
        <HowItWorksContent key={mode} mode={mode} />
      </div>
    </section>
  );
};

export default AdsHowItWorks;
